// Browser fingerprinting for anonymous user tracking
// This creates a semi-unique identifier based on browser characteristics

export async function generateFingerprint(): Promise<string> {
  const components: string[] = [];

  // Screen properties
  components.push(`screen:${screen.width}x${screen.height}x${screen.colorDepth}`);
  
  // Timezone
  components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  
  // Language
  components.push(`lang:${navigator.language}`);
  
  // Platform
  components.push(`platform:${navigator.platform}`);
  
  // Hardware concurrency (CPU cores)
  if (navigator.hardwareConcurrency) {
    components.push(`cores:${navigator.hardwareConcurrency}`);
  }
  
  // Device memory (if available)
  if ((navigator as any).deviceMemory) {
    components.push(`mem:${(navigator as any).deviceMemory}`);
  }
  
  // Touch support
  components.push(`touch:${navigator.maxTouchPoints || 0}`);
  
  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 200;
      canvas.height = 50;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Klartext', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Check', 4, 17);
      
      const dataUrl = canvas.toDataURL();
      components.push(`canvas:${hashString(dataUrl)}`);
    }
  } catch (e) {
    components.push('canvas:error');
  }
  
  // WebGL renderer (GPU info)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        components.push(`gpu:${hashString(renderer)}`);
      }
    }
  } catch (e) {
    components.push('gpu:error');
  }
  
  // Audio context fingerprint
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    components.push(`audio:${audioContext.sampleRate}`);
    audioContext.close();
  } catch (e) {
    components.push('audio:error');
  }
  
  // Combine all components and hash
  const fingerprint = components.join('|');
  return hashString(fingerprint);
}

// Simple hash function for fingerprint
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// Get or generate fingerprint (cached in sessionStorage)
export function getFingerprint(): string | null {
  const cached = sessionStorage.getItem('klartext-fingerprint');
  if (cached) {
    return cached;
  }
  
  // Generate async and cache
  generateFingerprint().then(fp => {
    sessionStorage.setItem('klartext-fingerprint', fp);
  });
  
  return null;
}

// Initialize fingerprint on load
export function initFingerprint(): void {
  if (!sessionStorage.getItem('klartext-fingerprint')) {
    generateFingerprint().then(fp => {
      sessionStorage.setItem('klartext-fingerprint', fp);
    });
  }
}
